import React from 'react';
import { IUser } from '../../Interfaces/IUser';
import { IUserGroup } from '../../Interfaces/IUserGroup';
import {
  Avatar,
  Box,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Group } from '@mui/icons-material';
import { ILegalRequirement } from '../../Interfaces/ILegalRequirement';
import {
  getUserAvatarProps,
  instantViewLegalTransaction,
  spanTsx,
  stringAvatar,
} from './helpers';

interface IProps {
  title: string;
  /** true = Spalte zeigt den Notar (Notary_idUser), false = Mitarbeiter/Gruppe */
  isNotary: boolean;
  userArray: IUser[];
  userGroupArray: IUserGroup[];
  legReqs: undefined | ILegalRequirement[];
  isLoadingLegReqs: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  /** Zeilen pro Seite – steuert, ob die "weiter"-Schaltfläche deaktiviert wird */
  rowsPerPage: number;
}

export const RequirementColumn: React.FC<IProps> = ({
  title,
  isNotary,
  userArray,
  userGroupArray,
  legReqs,
  isLoadingLegReqs,
  page,
  setPage,
  rowsPerPage,
}) => {
  const reqs = legReqs ?? [];
  const isLastPage = reqs.length < rowsPerPage;

  const getTsxForReq = (currentReq: ILegalRequirement) => {
    const testLegalTransaction = reqs.find(
      x => x.idLegalTransaction === currentReq.idLegalTransaction
    );
    const testUser = userArray.find(x =>
      isNotary
        ? currentReq.Notary_idUser === x.idUser
        : currentReq.Employee_idUser === x.idUser
    );
    let userGroup: undefined | IUserGroup = undefined;

    // Prüfen bei User === undefined, ob Usergroup
    if (testUser === undefined) {
      userGroup = userGroupArray.find(
        x => x.idUserGroup === currentReq.idUserGroup
      );
    }

    const avatarTsx =
      testUser !== undefined ? (
        <Avatar
          {...getUserAvatarProps(testUser)}
          alt={`${testUser.FirstName} ${testUser.LastName}`}
        />
      ) : (
        <Box sx={{ display: 'flex' }}>
          <Avatar
            {...stringAvatar(`${userGroup?.UserGroup}`)}
            alt={`${userGroup?.UserGroup}`}
          />
          <Group sx={{ ml: -2, mt: 2, zIndex: 100 }} />
        </Box>
      );

    if (
      testLegalTransaction !== undefined &&
      (testUser !== undefined || userGroup !== undefined)
    ) {
      return (
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(
              `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
              '_blank'
            )
          }
        >
          {instantViewLegalTransaction(
            testLegalTransaction!,
            currentReq.LegalRequirement +
              (currentReq.days_overdue
                ? ` (${currentReq.days_overdue} Tage)`
                : '') +
              `\n${currentReq.Title}`
          )}
          {avatarTsx}
        </Box>
      );
    } else if (
      testLegalTransaction === undefined &&
      (testUser !== undefined || userGroup !== undefined)
    ) {
      return (
        <>
          {spanTsx(
            '#FF0000',
            '#FF0000',
            'Fehler: ' + String(currentReq.idLegalTransaction)
          )}
          {avatarTsx}
        </>
      );
    } else if (
      testLegalTransaction !== undefined &&
      testUser === undefined &&
      userGroup === undefined
    ) {
      return (
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(
              `/legaltransaction/${testLegalTransaction!.idLegalTransaction}`,
              '_blank'
            )
          }
        >
          {instantViewLegalTransaction(testLegalTransaction)}
          {spanTsx(
            '#FF0000',
            '#FF0000',
            'Fehler: ' +
              (isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser))
          )}
        </Box>
      );
    } else {
      return (
        <>
          {spanTsx(
            '#FF0000',
            '#FF0000',
            'Fehler: ' + String(currentReq.idLegalTransaction)
          )}
          {spanTsx(
            '#FF0000',
            '#FF0000',
            'Fehler: ' +
              (isNotary
                ? String(currentReq.Notary_idUser)
                : String(currentReq.Employee_idUser))
          )}
        </>
      );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{title}</Typography>
        <IconButton
          size='small'
          title='Vorherige Maßnahmen anzeigen'
          disabled={page === 1}
          onClick={() => setPage(prev => prev - 1)}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          size='small'
          title='Nächste Maßnahmen anzeigen'
          disabled={isLastPage}
          onClick={() => setPage(prev => prev + 1)}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {isLoadingLegReqs ? (
        <Skeleton variant='rectangular' height={260} />
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Maßnahme</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reqs.length === 0 ? (
              <TableRow>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    Keine zu prüfenden Maßnahmen.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reqs.map(req => (
                <TableRow key={req.idLegalRequirement}>
                  <TableCell>{getTsxForReq(req)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
